import type { FlightCardData, FlightJourneySegment } from '@massilia/queries'

import { isKnownAirlineCode } from '@massilia/contracts'

import type { LuggageFilterValue, PaymentFilterValue, SortFilterValue } from '@/lib/schemas'

import type { DurationRange } from './duration-filter'
import type { PriceRange } from './price-filter'
import type { SegmentTimeFilter } from './time-filter'

/**
 * Compagnie retenue par le filtre « Compagnies » : la VENDEUSE (émettrice), celle
 * affichée en titre de la carte et déjà l'axe du bannissement côté serveur.
 *
 * Surtout PAS l'opérante du 1er tronçon (`airlineCode`) : sur un intermodal TGV Air
 * (Nantes → CDG en train, puis vol vers RUN) elle vaut AccesRail, jamais Corsair ni
 * Air Austral — le filtre proposait alors une compagnie que la carte n'affiche pas.
 *
 * Toutes les surfaces du filtre (liste, compteurs, blacklist, `availableAirlines`
 * envoyé au chat) DOIVENT passer par ici : deux axes différents et « sans Corsair »
 * ne retirerait rien.
 */
/** La compagnie sur laquelle une offre se filtre : son code IATA et son nom affiché. */
export interface FilterAirline {
  name: string
  code: string
}

export interface FlightFiltersState {
  durationRange?: DurationRange
  excludedAirlines: string[]
  luggage: LuggageFilterValue
  payment: PaymentFilterValue
  priceRange?: PriceRange
  stops: 'all' | 'direct' | 'one_stop_max' | 'two_stops_max'
  timeFilters: SegmentTimeFilter[]
}

// Helper function to filter offers based on filter state
export function applyFilters(
  offers: FlightCardData[],
  filters: FlightFiltersState
): FlightCardData[] {
  return offers.filter((offer) => {
    // Filter by price
    if (filters.priceRange) {
      const price = Number.parseFloat(offer.pricing.total.amount)
      if (price < filters.priceRange.min || price > filters.priceRange.max) {
        return false
      }
    }

    // Filter by duration (per segment)
    const { durationRange } = filters
    if (durationRange) {
      const allInRange = getAllSegments(offer).every((seg) => {
        const d = seg.totalDuration || 0
        return d >= durationRange.min && d <= durationRange.max
      })
      if (!allInRange) return false
    }

    // Filter by airline (blacklist) — sur la compagnie VENDEUSE, cf. getFilterAirline
    if (filters.excludedAirlines.includes(getFilterAirline(offer).code)) {
      return false
    }

    // Filter by payment type — même drapeau que la mention de la carte de vol :
    // `isInstallmentEligibleAtSearch` distingue le paiement en plusieurs fois
    // sans frais Massilia du 1 fois / plusieurs fois avec frais Oney-FLOA.
    if (filters.payment === 'massilia' && !offer.isInstallmentEligibleAtSearch) return false
    if (filters.payment === 'partner' && offer.isInstallmentEligibleAtSearch) return false

    // Filter by stops - check all segments including multi-destination
    let maxStops = Math.max(offer.outbound.stops, offer.return?.stops ?? 0)
    if (offer.segments && offer.segments.length > 0) {
      const segmentMaxStops = Math.max(...offer.segments.map((s) => s.stops))
      maxStops = Math.max(maxStops, segmentMaxStops)
    }

    switch (filters.stops) {
      case 'direct': {
        if (maxStops > 0) return false
        break
      }
      case 'one_stop_max': {
        if (maxStops > 1) return false
        break
      }
      case 'two_stops_max': {
        if (maxStops > 2) return false
        break
      }
      case 'all':
      default: {
        break
      }
    }

    // Filter by luggage
    if (filters.luggage === 'none') {
      if (offer.minCheckedBags !== 0) {
        return false
      }
    } else {
      const minCheckedBags = Number.parseInt(filters.luggage, 10)
      if (offer.minCheckedBags < minCheckedBags) {
        return false
      }
    }

    // Filter by time slots
    if (filters.timeFilters.length > 0) {
      const allSegments = getAllSegments(offer)

      for (const timeFilter of filters.timeFilters) {
        const segment = allSegments[timeFilter.segmentIndex]
        if (!segment) continue

        const depHour = getHourFromISOString(segment.departureTime)
        const arrHour = getHourFromISOString(segment.arrivalTime)

        // Check if departure time is in selected slots (only if slots are specified)
        if (
          timeFilter.departureTimeSlots.length > 0 &&
          !isHourInSlots(depHour, timeFilter.departureTimeSlots)
        ) {
          return false
        }

        // Check if arrival time is in selected slots (only if slots are specified)
        if (
          timeFilter.arrivalTimeSlots.length > 0 &&
          !isHourInSlots(arrHour, timeFilter.arrivalTimeSlots)
        ) {
          return false
        }
      }
    }

    return true
  })
}

// Format segment label
export function formatSegmentLabel(segment: FlightJourneySegment): string {
  return `${segment.departureCity} → ${segment.arrivalCity}`
}

// Helper function to extract all segments from flight offers
export function getAllSegmentsFromOffers(offers: FlightCardData[]): FlightJourneySegment[] {
  if (offers.length === 0) return []

  const offer = offers[0] // Assume all offers have the same trip structure

  if (offer.segments && offer.segments.length > 0) {
    // Multi-destination: use the segments array
    return offer.segments
  } else if (offer.return) {
    // Round-trip: outbound and return
    return [offer.outbound, offer.return]
  }
  // One-way: outbound only
  return [offer.outbound]
}

// Helper function to get duration bounds from offers
export function getDurationBounds(offers: FlightCardData[]): DurationRange {
  if (offers.length === 0) {
    return { max: 24 * 60, min: 0 } // 24 hours in minutes
  }

  const durations = offers.flatMap((offer) =>
    getAllSegments(offer).map((seg) => seg.totalDuration || 0)
  )

  return {
    max: Math.ceil(Math.max(...durations) / 30) * 30, // Round up to nearest 30 minutes
    min: Math.floor(Math.min(...durations) / 30) * 30, // Round down to nearest 30 minutes
  }
}

export function getFilterAirline(offer: FlightCardData): FilterAirline {
  // `extractValidatingAirlineInfo` (serveur) renvoie ce sentinelle quand l'offre ne
  // porte pas de `rate.vendor` : on retombe alors sur l'opérante puis la marketing.
  if (isKnownAirlineCode(offer.validatingAirlineCode)) {
    return {
      name: offer.validatingAirline?.trim() || offer.validatingAirlineCode,
      code: offer.validatingAirlineCode,
    }
  }
  if (offer.airlineCode) {
    return { name: offer.airline?.trim() || offer.airlineCode, code: offer.airlineCode }
  }
  return {
    name: offer.marketingAirline?.trim() || offer.marketingAirlineCode,
    code: offer.marketingAirlineCode,
  }
}

// Convert ISO time string to hour number (0-24)
export function getHourFromISOString(isoString: string): number {
  const date = new Date(isoString)
  return date.getHours() + date.getMinutes() / 60
}

// Helper function to get price bounds from offers
export function getPriceBounds(offers: FlightCardData[]): PriceRange {
  if (offers.length === 0) {
    return { max: 1000, min: 0 }
  }

  const prices = offers.map((offer) => Number.parseFloat(offer.pricing.total.amount))
  return {
    max: Math.ceil(Math.max(...prices)),
    min: Math.floor(Math.min(...prices)),
  }
}

// Helper function to get a specific segment from an offer
export function getSegmentFromOffer(
  offer: FlightCardData,
  segmentIndex: number
): FlightJourneySegment | null {
  const allSegments = getAllSegmentsFromOffers([offer])
  return allSegments[segmentIndex] || null
}

// Sort offers by price (ascending or descending). Returns original order if no sort is provided.
export function sortOffers(
  offers: FlightCardData[],
  sort: SortFilterValue | undefined
): FlightCardData[] {
  if (!sort) return offers
  const sorted = [...offers].sort(
    (a, b) => Number.parseFloat(a.pricing.total.amount) - Number.parseFloat(b.pricing.total.amount)
  )
  return sort === 'price-asc' ? sorted : sorted.reverse()
}

// Helper function to get all segments from a flight offer
function getAllSegments(offer: FlightCardData): FlightJourneySegment[] {
  const segments = [offer.outbound]
  if (offer.return) segments.push(offer.return)
  if (offer.segments) segments.push(...offer.segments)
  return segments
}

// Helper function to check if hour is in any selected time slot
function isHourInSlots(hour: number, slots: string[]): boolean {
  return slots.some((slot) => {
    switch (slot) {
      case '0-6': {
        return hour >= 0 && hour < 6
      }
      case '6-12': {
        return hour >= 6 && hour < 12
      }
      case '12-18': {
        return hour >= 12 && hour < 18
      }
      case '18-24': {
        return hour >= 18 && hour <= 24
      }
      default: {
        return false
      }
    }
  })
}
